import { css } from "@emotion/react";
import type { DOMNode, Text } from "html-react-parser";
import parse, { domToReact, Element } from "html-react-parser";
import { Link } from "react-router";
import { removeStopwords } from "stopword";

export const renderHighlightedText = (
  highlightedText: string,
  path: string,
  publicationId?: string,
  highlightTerm?: string,
) => {
  const terms = removeStopwords((highlightTerm || "").split(/\s+/))
    .filter(Boolean)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\"]/g, "\\$&"));

  const highlightRegex =
    terms.length > 0 ? new RegExp(`(${terms.join("|")})`, "gi") : null;

  const replace = (
    domNode: DOMNode,
  ): React.ReactElement | string | null | void => {
    if (domNode instanceof Element) {
      const { name, children } = domNode;

      if (name === "a" && domNode.attribs?.href?.startsWith("#")) {
        const hash = domNode.attribs.href;
        return (
          <Link
            to={{ pathname: `/pub/${path}`, hash }}
            css={() => css`
              text-decoration: underline;
            `}
          >
            {domToReact(children as DOMNode[], { replace })}
          </Link>
        );
      }

      // Special case for FRJ links make sure they open in a new tab
      // This is because FRJ links are external and should not be handled
      // by React Router
      if (name === "a" && publicationId === "FRJ") {
        return (
          <a
            href={domNode.attribs.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {domToReact(children as DOMNode[], { replace })}
          </a>
        );
      }

      return undefined;
    }

    if (domNode.type === "text" && highlightRegex) {
      const text = (domNode as Text).data;
      if (!highlightRegex.test(text)) return;

      const parts = text.split(highlightRegex);

      return (
        <>
          {parts.map((part, i) =>
            highlightRegex.test(part) ? (
              <span key={i} className="search-highlight">
                {part}
              </span>
            ) : (
              part
            ),
          )}
        </>
      );
    }
  };

  const parsed = parse(highlightedText, { replace });

  if (
    Array.isArray(parsed) &&
    parsed.some(
      (node) =>
        typeof node === "object" &&
        node !== null &&
        "type" in node &&
        node.type === "tr",
    )
  ) {
    const cleaned = parsed.filter(
      (node) => typeof node === "object" && node?.type === "tr",
    );
    return (
      <table>
        <tbody>{cleaned}</tbody>
      </table>
    );
  }

  return parsed;
};
